import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  input,
  signal
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { AuthAppService } from 'avn/modules/services/auth-app/auth-app.service';
import { BASE_SEARCH_PAGE_SESSION_KEY, Constants } from 'avn/modules/services/constants';
import { SharedCompsService } from 'avn/modules/services/shared-comps.service';
import { MegaMenuMobileComponent } from 'avn/modules/shared/components/mega-menu-mobile/mega-menu-mobile';
import { SearchBoxComponent } from 'avn/modules/shared/components/search-box/search-box';
import { User } from 'avn/modules/shared/interfaces/user';
import { Subject, filter, takeUntil } from 'rxjs';

const MOBILE_BREAKPOINT_PX = 768;

@Component({
  selector: 'app-header',
  templateUrl: './header.html',
  styleUrls: ['./header.scss'],
  imports: [NgClass, RouterLink, SearchBoxComponent],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent implements OnInit, OnDestroy {
  public workcoverLoggedUser: any;
  public showBusinessDashboard = true;
  public isAgreedProvidersTandC!: boolean;
  public branding = Constants.BRANDING;
  private readonly destroy$ = new Subject<void>();

  public readonly user = input<User>();

  public activeMenu: string | null = null;

  public readonly isDashboard = signal(false);
  public readonly isSearchEnabled = signal(false);
  public isServicePage = false;
  public readonly isOnlySearchPage = signal(false);
  public readonly isMobileView = signal(false);

  public constructor(
    public router: Router,
    private readonly authService: AuthAppService,
    private readonly dialog: MatDialog,
    private readonly sharedComps: SharedCompsService,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.authService.workcoverLoggedUser.pipe(takeUntil(this.destroy$)).subscribe((userData: any) => {
      this.isAgreedProvidersTandC = userData?.header_detail?.terms_and_pricing;
      this.workcoverLoggedUser = userData;
      this.cdr.markForCheck();
    });
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.setDefaultData();
      });
  }

  @HostListener('window:resize')
  public onResize(): void {
    this.checkScreenSize();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public ngOnInit(): void {
    this.setDefaultData();
    this.checkScreenSize();
  }

  public checkScreenSize(): void {
    // window is unavailable during SSR
    if (typeof window === 'undefined') {
      return;
    }
    this.isMobileView.set(window.innerWidth <= MOBILE_BREAKPOINT_PX);
    this.cdr.markForCheck();
  }
  public setDefaultData(): void {
    const urlSplit = this.router.url.split('/');
    if (urlSplit[1] === 'business' || urlSplit[1] === 'wellbeing') {
      this.isDashboard.set(true);
    } else {
      this.isDashboard.set(false);
    }
    if (
      urlSplit[1] === 'search' ||
      urlSplit[1] === 'category' ||
      urlSplit[1] === 'browse' ||
      urlSplit[1] === 'assistedbooking'
    ) {
      this.isSearchEnabled.set(true);
    } else {
      this.isSearchEnabled.set(false);
    }
    if (urlSplit[1] === 'search' || urlSplit[1] === 'browse' || urlSplit[1] === 'assistedbooking') {
      this.isOnlySearchPage.set(true);
    } else {
      this.isOnlySearchPage.set(false);
    }
    this.isServicePage = urlSplit[1] === 'search' || urlSplit[1] === 'browse' || urlSplit[1] === 'assistedbooking';
    this.cdr.markForCheck();
  }

  public checkIsSearchPage(): boolean {
    return this.isSearchEnabled();
  }

  public checkIsOnlySearchPage(): boolean {
    return this.isOnlySearchPage();
  }

  private getBaseSearchPage(): string {
    // sessionStorage is unavailable during SSR
    if (typeof sessionStorage === 'undefined') {
      return '';
    }
    return sessionStorage.getItem(BASE_SEARCH_PAGE_SESSION_KEY) || '';
  }

  public getLogoUrl(): string {
    const basePage = this.getBaseSearchPage();
    return basePage.split('?')[0];
  }

  public getLogoQueryParams(): any {
    const basePage = this.getBaseSearchPage();
    const queryString = basePage.includes('?') ? basePage.split('?')[1] : '';
    return this.sharedComps.parseQueryString(queryString);
  }

  public toggleMenu(menu: string): void {
    if (this.activeMenu === menu) {
      this.activeMenu = null;
    } else {
      this.activeMenu = menu;
    }
  }

  public menuClosed(menu: string): void {
    if (this.activeMenu === menu) {
      this.activeMenu = null;
    }
  }

  public openMobileMainMenu(menu: string): void {
    const backdropClass =
      this.isDashboard() || this.isSearchEnabled()
        ? ['mobile-mega-menu-backdrop', 'full']
        : 'mobile-mega-menu-backdrop';
    this.dialog.open(MegaMenuMobileComponent, {
      width: '100%',
      maxWidth: '375px',
      backdropClass: backdropClass,
      panelClass: ['mobile-mega-menu-panel', 'popup-container'],
      position: {
        top: '67px',
        right: '24px'
      },
      data: {
        menu
      }
    });
  }

  public loginPage(): void {
    this.router.navigate(['/login']);
  }

  public navigateToBusinessDashboard(): void {
    console.warn(this.workcoverLoggedUser);
    const businessId: any = this.workcoverLoggedUser?.businesses[0]?.id;
    const dashboardUrl = 'business/' + String(businessId) + '/profile';
    this.router.navigate([dashboardUrl]);
  }

  public navigateToLogin(): void {
    if (this.router.url.indexOf('/login?') === -1) {
      this.router.navigate(['/login'], { queryParams: { redirect: this.router.url } });
    } else {
      this.router.navigate(['/login'], { queryParams: { redirect: '' } });
    }
  }
  public navigateToProviderLogin(): void {
    if (this.router.url.indexOf('/login?') === -1) {
      this.router.navigate(['/login'], { queryParams: { redirect: this.router.url, action: 'provider_signin' } });
    } else {
      this.router.navigate(['/login'], { queryParams: { redirect: '', action: 'provider_signin' } });
    }
  }
  public navigateToSignup(): void {
    if (this.router.url.indexOf('/login?') === -1) {
      this.router.navigate(['/login'], { queryParams: { redirect: this.router.url, action: 'signup' } });
    } else {
      this.router.navigate(['/login'], { queryParams: { redirect: '', action: 'signup' } });
    }
  }
}
